'use client';

import { rangeForPeriod } from '@slims/shared/calculations';
import type { DashboardPeriod, DashboardSummaryRange } from '@slims/shared/calculations';
import type { DashboardKpiSummary } from '@slims/shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

// `rangeForPeriod` e il tipo periodo vivono in `@slims/shared` perché li usa
// anche il mobile; ri-esportati qui per non toccare i call-site web esistenti.
export { rangeForPeriod } from '@slims/shared/calculations';
export type { DashboardPeriod } from '@slims/shared/calculations';

const DASHBOARD_SOURCE_TABLES = [
	{ topic: 'dashboard-summary-readings', table: 'energy_readings' },
	{ topic: 'dashboard-summary-estimated', table: 'estimated_data' },
	{ topic: 'dashboard-summary-gse', table: 'gse_official_data' },
	{ topic: 'dashboard-summary-bill', table: 'energy_bill_readings' },
	{ topic: 'dashboard-summary-members', table: 'members' },
	{ topic: 'dashboard-summary-meters', table: 'meters' },
	{ topic: 'dashboard-summary-config', table: 'organizations' },
] as const;

/** React Query key for the dashboard KPI summary of one org over a period. */
export const dashboardSummaryKey = (
	orgId: string | null | undefined,
	period: DashboardPeriod,
	range?: DashboardSummaryRange,
) => ['dashboard-summary', orgId, period, range ?? null] as const;

/**
 * ADR-006: backed by React Query — cached and deduped across panels and
 * navigations. The seven source-table channels invalidate the entry so the KPIs
 * follow live writes.
 */
export function useDashboardSummary(
	orgId: string | null | undefined,
	period: DashboardPeriod = 'oggi',
	/**
	 * Finestra esplicita al posto di quella del periodo, per esempio un mese
	 * passato o «oggi» con la quota della sola giornata (MySlims, N-19).
	 */
	range?: DashboardSummaryRange,
) {
	const queryClient = useQueryClient();
	const loaded = useAuthStore((state) => state.loaded);
	const enabled = loaded && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: dashboardSummaryKey(orgId, period, range),
		enabled,
		queryFn: () =>
			api.get<DashboardKpiSummary>('/api/energy/dashboard-summary', {
				orgId: orgId as string,
				...(range ?? rangeForPeriod(period)),
			}),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	useEffect(() => {
		if (!enabled) return;
		const unsubs = DASHBOARD_SOURCE_TABLES.map(({ topic, table }) =>
			subscribeToTable(topic, { event: '*', schema: 'public', table }, () => {
				queryClient.invalidateQueries({ queryKey: dashboardSummaryKey(orgId, period, range) });
			}),
		);
		return () => {
			for (const unsub of unsubs) unsub();
		};
	}, [enabled, orgId, period, range, queryClient]);

	return {
		summary: data ?? null,
		isLoading: enabled ? isLoading : false,
		error: error ? (error instanceof Error ? error.message : 'Errore nel caricamento KPI dashboard') : null,
		refetch,
	};
}
